import { useTranslation } from "react-i18next";
import oral from "../../resources/img/oral.svg";
import gastro from "../../resources/img/gastro.svg";
import lungs from "../../resources/img/lungs.svg";
import heart from "../../resources/img/heart.svg";
import allergies from "../../resources/img/allergies.svg";
import movement from "../../resources/img/movement.svg";
import neurology from "../../resources/img/neurology.svg";
import visual from "../../resources/img/visual.svg";
import attention from "../../resources/img/attention.svg";
import pain from "../../resources/img/pain.svg";
import mood from "../../resources/img/mood.svg";
import urinary from "../../resources/img/urinary.svg";
import sexual from "../../resources/img/sexual.svg";
import other from "../../resources/img/other.svg";
import general from "../../resources/img/symptom.svg";
import { useContext, useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import InitContext from "../pages/InitContext";
import SymptomParams from "./SymptomParams";
import Symptom from "./Symptom";
import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import { Consts } from "../pages/consts";
import useDialogDrag from "../../hooks/useDialogDrag";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

const convertPromsTypes = (symptoms) => {
  return symptoms?.map((symptom) => {
    let src;

    switch (symptom?.type) {
      case "cardiovascular_issues":
        src = heart;
        break;
      case "cognitive_issues":
        src = attention;
        break;
      case "gastrointestinal_tract_issues":
        src = gastro;
        break;
      case "general_health_complaints":
        src = general;
        break;
      case "issues_concerning_the_movement_apparatus":
        src = movement;
        break;
      case "mood_disorders":
        src = mood;
        break;
      case "nervous_system":
        src = neurology;
        break;
      case "oral_issues":
        src = oral;
        break;
      case "pain":
        src = pain;
        break;
      case "reproductive_organs_issues":
        src = sexual;
        break;
      case "respiratory_tract_and_breathing":
        src = lungs;
        break;
      case "sensory_organs_issues":
        src = visual;
        break;
      case "skin_hair_issues":
        src = allergies;
        break;
      case "urinary_tract_issues":
        src = urinary;
        break;
      case otherComplaintsKey:
        src = other;
        break;
      default:
        src = null;
    }

    return {
      src,
      type: symptom.type,
    };
  });
};

export const otherComplaintsKey = "other_health_complaints";

const mergeOtherComplaints = (symptoms, symptomsList) => {
  const normalizedSymptoms = Array.isArray(symptoms)
    ? symptoms.map((item) => ({
      ...item,
      symptoms: Array.isArray(item?.symptoms) ? [...item.symptoms] : [],
    }))
    : [];

  const additionalOtherComplaints = symptomsList
    ?.filter((symptom) => symptom?.type === otherComplaintsKey && symptom?.term)
    ?.map((symptom) => ({ term: symptom.term })) ?? [];

  const existingOtherComplaintsIndex = normalizedSymptoms.findIndex(
    (item) => item?.type === otherComplaintsKey,
  );

  if (existingOtherComplaintsIndex >= 0) {
    const existingTerms = new Set(
      normalizedSymptoms[existingOtherComplaintsIndex]?.symptoms?.map((symptom) => symptom?.term),
    );

    normalizedSymptoms[existingOtherComplaintsIndex].symptoms.push(
      ...additionalOtherComplaints.filter((symptom) => !existingTerms.has(symptom.term)),
    );

    return normalizedSymptoms;
  }

  return [
    ...normalizedSymptoms,
    {
      type: otherComplaintsKey,
      symptoms: additionalOtherComplaints,
    },
  ];
};

const SymptomModules = ({
  onSaveNewSymptoms,
  showDialog,
  hcpView,
  symptomsList,
}) => {
  const { t, i18n } = useTranslation();
  const [symptomParamsDialogVisible, setSymptomParamsDialogVisible] = useState(false);
  const [selectedSymptomParams, setSelectedSymptomParams] = useState([]);
  const [selectedParamsDialogVisible, setSelectedParamsDialogVisible] = useState(false);
  const [promsSymptoms, setPromsSymptoms] = useState([]);
  const [symptomParams, setSymptomParams] = useState([]);
  const [selectedSymptomType, setSelectedSymptomType] = useState("");
  const [selectedSearchSymptom, setSelectedSearchSymptom] = useState(null);
  const context = useContext(InitContext);
  const { loading, error, clearError, getPromsJson } = useMmcService();
  const { dragStyle, isDragging, onDragHandleMouseDown } = useDialogDrag(showDialog);

  const allSymptoms = useMemo(
    () => mergeOtherComplaints(promsSymptoms, symptomsList),
    [promsSymptoms, symptomsList],
  );

  const symptomTypes = useMemo(
    () => convertPromsTypes(allSymptoms),
    [allSymptoms],
  );

  const searchableSymptoms = useMemo(() => {
    const collator = new Intl.Collator(i18n.language, { sensitivity: "base" });

    return allSymptoms
      ?.flatMap((item) => item?.symptoms?.map((symptom) => ({
        value: `${item?.type}-${symptom?.term}`,
        label: symptom?.term ? t(symptom.term) : "",
        categoryLabel: t(item?.type),
        type: item?.type,
        symptom: {
          ...symptom,
          type: symptom?.type ?? item?.type,
        },
      })) ?? [])
      ?.filter((item) => item?.label)
      ?.sort((a, b) => (
        collator.compare(a.label, b.label) || collator.compare(a.categoryLabel, b.categoryLabel)
      ));
  }, [allSymptoms, i18n.language, t]);

  const resetDialogState = () => {
    setSymptomParamsDialogVisible(false);
    setSelectedSymptomParams([]);
    setSelectedParamsDialogVisible(false);
    setSymptomParams([]);
    setSelectedSymptomType("");
    setSelectedSearchSymptom(null);
  };

  useEffect(() => {
    clearError();

    getPromsJson(context?.token).then((data) => {
      setPromsSymptoms(Array.isArray(data) ? data : []);
    });
  }, []);

  useEffect(() => {
    if (!showDialog) {
      resetDialogState();
    }
  }, [showDialog]);

  const openSymptomDialog = (params, type) => {
    if (!Array.isArray(params) || params.length === 0) {
      return;
    }

    setSelectedSymptomType(type);
    setSelectedSymptomParams(params);
    setSelectedParamsDialogVisible(true);
    setSymptomParamsDialogVisible(false);
  };

  const symptomTypeSelected = (type) => {
    const selectedType = allSymptoms?.find((item) => item?.type === type);

    if (!selectedType) {
      return;
    }

    setSelectedSearchSymptom(null);
    setSelectedSymptomType(type);
    setSymptomParams(selectedType?.symptoms ?? []);
    setSelectedParamsDialogVisible(false);
    setSymptomParamsDialogVisible(true);
  };

  function handleSymptomParamsSelected(params) {
    openSymptomDialog(params, selectedSymptomType);
  }

  function handleSearchSymptomSelected(_, option) {
    setSelectedSearchSymptom(option);

    if (!option) {
      return;
    }

    openSymptomDialog([option.symptom], option.type);
  }

  const errorMessage = error && (
    <ErrorMessage message={t("error_by_retrieving_all_symptoms")} />
  );

  const spinner = loading && <Spinner />;

  const dialogTitle = selectedParamsDialogVisible || symptomParamsDialogVisible
    ? t(selectedSymptomType || "symptoms")
    : (hcpView
      ? t("please_select_a_type_of_the_symptom")
      : t("please_select_a_type_of_your_symptom"));

  const handleCloseDialog = () => {
    resetDialogState();
    onSaveNewSymptoms(null);
  };

  return (
    <div
      className="w3-modal"
      style={{ display: showDialog ? Consts.BLOCK : Consts.NONE }}
    >
      <div
        className={`w3-modal-content w3-card-4 w3-animate-zoom w3-theme-l3 dialog-medium w3-center dialog-draggable ${isDragging ? "dialog-draggable--dragging" : ""}`}
        style={dragStyle}
      >
        <header className="dialog-drag-header" onMouseDown={onDragHandleMouseDown}>
          <strong className="dialog-drag-header__title">{dialogTitle}</strong>
          <button
            type="button"
            className="dialog-drag-close"
            onClick={handleCloseDialog}
            aria-label={t("close", { defaultValue: "Close" })}
            title={t("close", { defaultValue: "Close" })}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>
        {!selectedParamsDialogVisible && !symptomParamsDialogVisible && (
          <div
            className="w3-container w3-padding-small"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Autocomplete
              options={searchableSymptoms}
              value={selectedSearchSymptom}
              onChange={handleSearchSymptomSelected}
              fullWidth
              disabled={loading || searchableSymptoms.length === 0}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              getOptionLabel={(option) => option?.label ?? ""}
              noOptionsText={t("no_symptoms_found", { defaultValue: "No health complaints found" })}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <span>{option.label}</span>
                    <small style={{ color: "rgba(13, 59, 102, 0.7)" }}>{option.categoryLabel}</small>
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={t("search_symptom", { defaultValue: "Search for a health complaint" })}
                  placeholder={t("search_symptom_placeholder", { defaultValue: "Start typing to search all health complaints" })}
                />
              )}
              sx={{
                marginTop: 0.5,
                marginBottom: 0.5,
                maxWidth: "560px",
                marginLeft: "auto",
                marginRight: "auto",
                "& .MuiInputBase-root": {
                  minHeight: "42px",
                  borderRadius: "10px",
                  backgroundColor: "#ffffff",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.95rem",
                },
                "& .MuiAutocomplete-input": {
                  paddingTop: "8px !important",
                  paddingBottom: "8px !important",
                },
              }}
            />
          </div>
        )}
        {selectedParamsDialogVisible ? (
          <Symptom
            symptomParams={selectedSymptomParams}
            category={selectedSymptomType}
            onSaveNewSymptoms={onSaveNewSymptoms}
          />
        ) : (
          <>
            {symptomParamsDialogVisible ? (
              <SymptomParams
                symptomParams={symptomParams}
                setSymptomParams={setSymptomParams}
                category={symptomTypes?.find((item) => item?.type === selectedSymptomType)?.type}
                onSymptomParamsSelected={handleSymptomParamsSelected}
              />
            ) : (
              <>
                {errorMessage}
                {spinner}
                <div className="w3-container form-content-scroll w3-center">
                  {!hcpView && (
                    <div className="w3-row-padding">
                      <div className="w3-panel w3-card-4 w3-center disclaimer-background">
                        <label className="disclaimer-text">
                          ⚠️ {t("symptoms_disclaimer_1")}{" "}
                          {t("symptoms_disclaimer_2")}
                        </label>
                      </div>
                    </div>
                  )}
                  <div className="symptom-grid w3-margin-top">
                    {symptomTypes?.map((item, i) => (
                      <div
                        className="w3-display-container w3-panel w3-card-4 w3-center symptom-panel"
                        key={`item ${i}`}
                        onClick={() => symptomTypeSelected(item?.type)}
                      >
                        <label className="home-text">{t(item?.type)}</label>
                        <img
                          src={item?.src}
                          alt={t(item?.type)}
                          className="w3-display-bottommiddle home-icon"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SymptomModules;
